import { useEffect, useMemo, useState } from 'react';
import { getBlogCategories, getBlogPosts } from '../api/client';
import { BlogCategory, BlogPostInfo } from '../api/types';
import BlurFade from '../components/BlurFade';
import '../assets/styles/Blog.scss';
import { useLanguage } from '../i18n/LanguageContext';
import BlogCard from './BlogCard';

const BLUR_FADE_DELAY = 0.04;
const PAGE_SIZE = 5;

function BlogList() {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState<BlogPostInfo[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    Promise.all([getBlogPosts(), getBlogCategories()])
      .then(([postsRes, categoriesRes]) => {
        setPosts(postsRes);
        setCategories(categoriesRes);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const categoryLabel = useMemo(() => {
    const map = new Map(categories.map((c) => [c.slug, lang === 'en' ? c.name_en : c.name_tr]));
    return (slug: string) => map.get(slug) ?? slug;
  }, [categories, lang]);

  const sortedPosts = useMemo(() => {
    return [...posts].sort((a, b) => {
      const ad = a.published_at ? new Date(a.published_at).getTime() : 0;
      const bd = b.published_at ? new Date(b.published_at).getTime() : 0;
      return bd - ad;
    });
  }, [posts]);

  const usedCategories = useMemo(() => {
    const slugs = new Set<string>();
    sortedPosts.forEach((p) => slugs.add(p.category));
    return Array.from(slugs);
  }, [sortedPosts]);

  const filteredPosts = activeCategory ? sortedPosts.filter((p) => p.category === activeCategory) : sortedPosts;

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    setPage(1);
  }, [activeCategory]);

  const paginatedPosts = filteredPosts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <section id="blog" className="page">
      <BlurFade delay={BLUR_FADE_DELAY} className="page-head">
        <div className="eyebrow eyebrow-acc">
          {t.blog.eyebrow}
          {!loading && !error && ` · ${posts.length} ${t.blog.postsLabel}`}
        </div>
        <h1 className="page-title">{t.blog.heading}</h1>
        <p className="page-sub">{t.blog.subheading}</p>
      </BlurFade>

      {usedCategories.length > 0 && (
        <BlurFade delay={BLUR_FADE_DELAY * 2}>
          <div className="blog-filters">
            <button className={`blog-tag${!activeCategory ? ' active' : ''}`} onClick={() => setActiveCategory(null)}>
              {t.blog.allCategories}
            </button>
            {usedCategories.map((category) => (
              <button
                key={category}
                className={`blog-tag${activeCategory === category ? ' active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                {categoryLabel(category)}
              </button>
            ))}
          </div>
        </BlurFade>
      )}

      {loading && <p className="blog-status">{t.blog.loading}</p>}
      {!loading && error && <p className="blog-status">{t.blog.error}</p>}
      {!loading && !error && filteredPosts.length === 0 && <p className="blog-status">{t.blog.noPostsFound}</p>}

      <BlurFade delay={BLUR_FADE_DELAY * 3}>
        <div className="blog-rows">
          {paginatedPosts.map((post, idx) => (
            <BlogCard
              key={post.id}
              post={post}
              index={(currentPage - 1) * PAGE_SIZE + idx + 1}
              categoryLabel={categoryLabel(post.category)}
            />
          ))}
        </div>
      </BlurFade>

      {!loading && !error && totalPages > 1 && (
        <BlurFade delay={BLUR_FADE_DELAY * 4}>
          <div className="blog-pagination">
            <span className="blog-pagination-status">
              {t.blog.pageOf.replace('{page}', String(currentPage)).replace('{total}', String(totalPages))}
            </span>
            <div className="blog-pagination-buttons">
              <button
                className="blog-pagination-btn"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t.blog.previous}
              </button>
              <button
                className="blog-pagination-btn"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                {t.blog.next}
              </button>
            </div>
          </div>
        </BlurFade>
      )}
    </section>
  );
}

export default BlogList;
